import React from 'react';
import { useTheme } from '../../hooks/useTheme';
import iconColor from '../../assets/brand/festomi-icon.png';
import iconWhite from '../../assets/brand/festomi-icon-white.png';
import lockupColor from '../../assets/brand/festomi-logo.png';
import lockupWhite from '../../assets/brand/festomi-logo-white.png';
import stackedColor from '../../assets/brand/festomi-logo-stacked.png';
import stackedWhite from '../../assets/brand/festomi-logo-stacked-white.png';
import wordmarkColor from '../../assets/brand/festomi-wordmark.png';
import wordmarkWhite from '../../assets/brand/festomi-wordmark-white.png';

/**
 * `icon` = mark only, `lockup` = mark + wordmark side by side,
 * `stacked` = mark over wordmark, `wordmark` = type only.
 *
 * `wordmark` exists so a reverse lockup can pair the COLOR mark with a white
 * wordmark — the flat `-white` lockup throws away the brand gradient, and the
 * color lockup's indigo type is unreadable on a dark surface. See SiteLogo.
 */
export type BrandLogoVariant = 'icon' | 'lockup' | 'stacked' | 'wordmark';
/** `auto` follows the light/dark mode; the deck's reverse art is the white one. */
export type BrandLogoTone = 'auto' | 'color' | 'white';

interface BrandLogoProps {
    variant?: BrandLogoVariant;
    tone?: BrandLogoTone;
    /** Rendered height in px; width follows the artwork's aspect ratio. */
    height?: number;
    className?: string;
    style?: React.CSSProperties;
}

const ART: Record<BrandLogoVariant, { color: string; white: string }> = {
    icon: { color: iconColor, white: iconWhite },
    lockup: { color: lockupColor, white: lockupWhite },
    stacked: { color: stackedColor, white: stackedWhite },
    wordmark: { color: wordmarkColor, white: wordmarkWhite },
};

/**
 * The Festomi logo, per `Festimo Branding.pdf`. Prefer this over hand-rolled
 * marks so every surface stays on the same artwork.
 */
const BrandLogo: React.FC<BrandLogoProps> = ({
    variant = 'lockup',
    tone = 'auto',
    height = 32,
    className,
    style,
}) => {
    const { isDark } = useTheme();
    const resolved = tone === 'auto' ? (isDark ? 'white' : 'color') : tone;

    return (
        <img
            src={ART[variant][resolved]}
            alt="Festomi"
            className={className}
            style={{ height, width: 'auto', display: 'block', ...style }}
            draggable={false}
        />
    );
};

export default BrandLogo;
